import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import type { AppUser } from '@/domain/user'
import { useServices } from './ServiceContext'

interface AuthContextValue {
  currentUser: AppUser | null
  isLoading: boolean
  login: (userId: string) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProviderWrapper({ children }: { children: ReactNode }) {
  const { auth } = useServices()
  const [currentUser, setCurrentUser] = useState<AppUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    setCurrentUser(auth.getCurrentUser())
    setIsLoading(false)
    return auth.subscribe(setCurrentUser)
  }, [auth])

  const login = async (userId: string) => {
    await auth.login(userId)
  }

  const logout = async () => {
    await auth.logout()
  }

  return (
    <AuthContext.Provider value={{ currentUser, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProviderWrapper')
  return ctx
}
